import type { ReactNode } from 'react';
import { useIntl } from 'react-intl';
import { Banner, StatusBadge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@immo/ui-kit';
import type {
  LandIqDataRunChunkStat,
  LandIqDataRunChunkStats,
  LandIqDataRunCommandProgress,
  LandIqDataRunTimings,
} from '@immo/api-contract';
import { ClockIcon } from '@/components/layout/icons';
import { useTranslations } from '@/i18n/use-translations';
import { RunProgressBar } from '@/components/landiq/run-progress-bar';
import { formatDuration } from '@/lib/landiq/format-duration';
import { isProgressStale, progressShare } from '@/lib/landiq/run-metrics';

const NOT_AVAILABLE = '—';
const PANEL = 'rounded-md border border-border bg-surface px-4 py-3 text-sm';

function useFormats() {
  const intl = useIntl();
  const at = (iso: string, withYear: boolean) =>
    intl.formatDate(iso, {
      day: '2-digit',
      month: '2-digit',
      ...(withYear ? { year: 'numeric' } : {}),
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Europe/Berlin',
    });

  return {
    number: (value: number) => intl.formatNumber(value),
    rate: (value: number) => intl.formatNumber(value, { maximumFractionDigits: 1 }),
    at,
  };
}

function Section({ title, children }: { readonly title: string; readonly children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-text-secondary">{title}</h2>
      {children}
    </section>
  );
}

function Fact({ label, children }: { readonly label: string; readonly children: ReactNode }) {
  return (
    <div className="flex items-baseline gap-3">
      <dt className="w-[150px] shrink-0 text-text-tertiary">{label}</dt>
      <dd className="min-w-0 break-words tabular-nums text-text-primary">{children}</dd>
    </div>
  );
}

/** Label over value, one column each: the times and the chunk stats read as a row of figures. */
function Figures({ figures }: { readonly figures: ReadonlyArray<{ label: string; value: ReactNode }> }) {
  return (
    <dl className={`${PANEL} grid gap-3`} style={{ gridTemplateColumns: `repeat(${figures.length}, minmax(0, 1fr))` }}>
      {figures.map(({ label, value }) => (
        <div key={label} className="flex flex-col gap-0.5">
          <dt className="text-text-tertiary">{label}</dt>
          <dd className="text-base font-medium tabular-nums text-text-primary">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

/** The command's latest progress snapshot, in full. A snapshot older than 20 min on a running run is flagged. */
export function RunProgress({
  progress,
  running,
}: {
  readonly progress: LandIqDataRunCommandProgress;
  readonly running: boolean;
}) {
  const t = useTranslations('pages.admin.landiq-data-run.metrics');
  const format = useFormats();
  const share = progressShare(progress);
  const stale = isProgressStale(progress, running, Date.now());
  const done = format.number(progress.done);

  return (
    <Section title={t('progress')}>
      <div className={`${PANEL} flex flex-col gap-3`}>
        {stale && (
          <Banner
            variant="warning"
            icon={<ClockIcon />}
            title={t('stale-banner', { age: formatDuration(Date.now() - Date.parse(progress.updatedAt)) })}
          />
        )}
        <div className="flex items-baseline justify-between gap-4">
          <span className="min-w-0 break-words">
            <span className="font-mono text-text-primary">{progress.phase}</span>{' '}
            <span className="text-text-tertiary">
              {t('phase-since', { time: format.at(progress.phaseStartedAt, false) })}
            </span>
          </span>
          <span className="shrink-0 font-semibold tabular-nums text-text-primary">
            {progress.total === null || share === null
              ? t('done-only', { done })
              : t('done-of', { done, total: format.number(progress.total), percent: Math.round(share * 100) })}
          </span>
        </div>
        {share !== null && (
          <RunProgressBar
            share={share}
            stale={stale}
            size="md"
            label={t('progress-label', { phase: progress.phase })}
          />
        )}
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2">
          <Fact label={t('labels.rate')}>
            {progress.itemsPerMinute === null
              ? NOT_AVAILABLE
              : t('rate-long', { rate: format.rate(progress.itemsPerMinute) })}
          </Fact>
          <Fact label={t('labels.eta')}>
            {progress.etaAt === null ? NOT_AVAILABLE : format.at(progress.etaAt, true)}
          </Fact>
          <Fact label={t('labels.requests')}>
            {t('requests', {
              live: format.number(progress.requestsLive),
              replayed: format.number(progress.requestsReplayed),
            })}
          </Fact>
          <Fact label={t('labels.retries')}>{format.number(progress.retries)}</Fact>
          <Fact label={t('labels.updated')}>
            <span className={stale ? 'text-warning-700' : undefined}>{format.at(progress.updatedAt, true)}</span>
          </Fact>
        </dl>
      </div>
    </Section>
  );
}

/** Queue, command, import and total. A stage LandIQ has no moment for shows a dash. */
export function RunTimes({ timings }: { readonly timings: LandIqDataRunTimings }) {
  const t = useTranslations('pages.admin.landiq-data-run.metrics');
  const duration = (ms: number | null) => (ms === null ? NOT_AVAILABLE : formatDuration(ms));

  return (
    <Section title={t('times')}>
      <Figures
        figures={[
          { label: t('stages.queued'), value: duration(timings.queuedMs) },
          { label: t('stages.command'), value: duration(timings.commandMs) },
          { label: t('stages.import'), value: duration(timings.importMs) },
          { label: t('stages.total'), value: <span className="font-semibold">{duration(timings.totalMs)}</span> },
        ]}
      />
    </Section>
  );
}

/** An import run's chunk summary. Rows written stays a dash until an importer reports it — never 0. */
export function RunChunkStats({ stats }: { readonly stats: LandIqDataRunChunkStats }) {
  const t = useTranslations('pages.admin.landiq-data-run.metrics');
  const format = useFormats();
  const duration = (ms: number | null) => (ms === null ? NOT_AVAILABLE : formatDuration(ms));

  return (
    <Section title={t('chunk-stats')}>
      <Figures
        figures={[
          { label: t('chunk-labels.seen'), value: format.number(stats.seen) },
          { label: t('chunk-labels.imported'), value: format.number(stats.imported) },
          {
            label: t('chunk-labels.failed'),
            value: (
              <span className={stats.failed > 0 ? 'text-danger-700' : undefined}>{format.number(stats.failed)}</span>
            ),
          },
          {
            label: t('chunk-labels.rows'),
            value: stats.rowsWritten === null ? NOT_AVAILABLE : format.number(stats.rowsWritten),
          },
          { label: t('chunk-labels.p50'), value: duration(stats.p50DurationMs) },
          { label: t('chunk-labels.max'), value: duration(stats.maxDurationMs) },
        ]}
      />
    </Section>
  );
}

/** The run's slowest chunks as LandIQ ranks them; a failed one carries its error on a second line. */
export function SlowestChunks({ chunks }: { readonly chunks: ReadonlyArray<LandIqDataRunChunkStat> }) {
  const t = useTranslations('pages.admin.landiq-data-run.metrics');
  const format = useFormats();
  const right = 'text-right';

  return (
    <Section title={t('slowest')}>
      <div className="rounded-md border border-border bg-surface">
        <Table style={{ tableLayout: 'fixed' }}>
          <colgroup>
            <col />
            <col style={{ width: 150 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 90 }} />
            <col style={{ width: 120 }} />
          </colgroup>
          <TableHeader>
            <TableRow>
              <TableHead>{t('slowest-columns.chunk')}</TableHead>
              <TableHead>{t('slowest-columns.outcome')}</TableHead>
              <TableHead className={right}>{t('slowest-columns.duration')}</TableHead>
              <TableHead className={right}>{t('slowest-columns.attempts')}</TableHead>
              <TableHead className={right}>{t('slowest-columns.rows')}</TableHead>
              <TableHead>{t('slowest-columns.finished')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {chunks.map((chunk) => (
              <TableRow key={chunk.chunkPath}>
                <TableCell className="align-top">
                  <div className="truncate font-mono text-sm" title={chunk.chunkPath}>
                    {chunk.chunkPath}
                  </div>
                  {chunk.error !== null && (
                    <div className="mt-1 break-words font-mono text-xs text-danger-700">{chunk.error}</div>
                  )}
                </TableCell>
                <TableCell className="align-top">
                  <StatusBadge
                    label={t(`chunk-outcomes.${chunk.outcome}`)}
                    tone={chunk.outcome === 'FAILED' ? 'danger' : 'success'}
                    size="sm"
                  />
                </TableCell>
                <TableCell className={`${right} align-top tabular-nums`}>{formatDuration(chunk.durationMs)}</TableCell>
                <TableCell className={`${right} align-top tabular-nums`}>{format.number(chunk.attempts)}</TableCell>
                <TableCell className={`${right} align-top tabular-nums`}>
                  {chunk.rowsWritten === null ? NOT_AVAILABLE : format.number(chunk.rowsWritten)}
                </TableCell>
                <TableCell className="align-top tabular-nums">{format.at(chunk.finishedAt, false)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Section>
  );
}
